import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Check, X, Trash2, Copy, Pencil } from "lucide-react";
import { useLocation } from "wouter";

const TIPO_LABELS = {
  venda_mercadoria: "Venda de mercadoria",
  venda_consumidor_final: "Venda para consumidor final",
  venda_industrializacao: "Venda para industrialização",
  remessa_armazem: "Remessa para armazém",
  retorno_simbolico: "Retorno simbólico",
  transferencia: "Transferência",
  estorno_venda: "Estorno de venda",
};

const TIPO_COLORS = {
  venda_mercadoria: "bg-blue-100 text-blue-800",
  venda_consumidor_final: "bg-green-100 text-green-800",
  venda_industrializacao: "bg-purple-100 text-purple-800",
  remessa_armazem: "bg-orange-100 text-orange-800",
  retorno_simbolico: "bg-yellow-100 text-yellow-800",
  transferencia: "bg-indigo-100 text-indigo-800",
  estorno_venda: "bg-red-100 text-red-800",
};

const GATILHO_LABELS: Record<string, string> = {
  consumidor_final: "Venda para consumidor final",
  sem_st: "Venda sem ST",
  com_st: "Venda com ST",
  alc: "ALC (Área de Livre Comércio)",
  zona_franca: "Zona Franca de Manaus",
  amazonia_ocidental: "AO (Amazônia Ocidental)",
  nenhum: "Nenhum",
};

const GATILHO_OPTIONS = Object.entries(GATILHO_LABELS).map(([value, label]) => ({ value, label }));

const emptyRegra = {
  csosnMesmoEstado: "",
  csosnInterestadual: "",
  cfopMesmoEstado: "",
  cfopInterestadual: "",
  gatilho: "nenhum",
};

export default function OperationNature() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isEmitenteModalOpen, setIsEmitenteModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Regras locais (pendentes de salvar)
  const [regras, setRegras] = useState<any[]>([]);
  const [novaRegra, setNovaRegra] = useState({ ...emptyRegra });
  const [editingRegraId, setEditingRegraId] = useState<number | null>(null);
  const [editingRegraData, setEditingRegraData] = useState({ ...emptyRegra });
  const [duplicatingFromId, setDuplicatingFromId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    nome: "",
    emitenteId: 0,
    emitenteNome: "",
    tipo: "venda_mercadoria" as keyof typeof TIPO_LABELS,
    movimentaEstoque: true,
    geraFinanceiro: true,
    observacoes: "",
    ativo: true,
    nfeProximoNumero: 1,
    nfeSerie: "001",
    tipoNota: "saida" as "entrada" | "saida",
    ambienteNfe: "homologacao" as "producao" | "homologacao",
    consumidorFinal: true,
    presenca: "presencial" as "nao_presencial" | "presencial" | "internet" | "outros",
    mod: 55,
    tpImp: 1,
    finNFe: 1,
    indIntermed: 0,
    indTot: 1,
    modFrete: 9,
  });

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Queries
  const { data: operationNatures = [], refetch } = trpc.operationNatures.list.useQuery();
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();

  // Query de regras — só busca quando há um editingId
  const { data: regrasServidor = [], refetch: refetchRegras } = trpc.operationNatures.listRegras.useQuery(
    { operationNatureId: editingId ?? 0 },
    { enabled: !!editingId }
  );

  // Query de regras para duplicação — busca regras da natureza original
  const { data: regrasDuplicar = [] } = trpc.operationNatures.listRegras.useQuery(
    { operationNatureId: duplicatingFromId ?? 0 },
    { enabled: !!duplicatingFromId }
  );

  // Sincronizar regras do servidor quando abre para edição
  const regrasServidorStr = JSON.stringify(regrasServidor);
  useEffect(() => {
    if (editingId) {
      setRegras(regrasServidor);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regrasServidorStr, editingId]);

  // Preencher regras ao duplicar
  const regrasDuplicarStr = JSON.stringify(regrasDuplicar);
  useEffect(() => {
    if (duplicatingFromId && regrasDuplicar.length > 0) {
      setRegras(regrasDuplicar.map((r: any) => ({ ...r, id: Date.now() + Math.random() })));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regrasDuplicarStr, duplicatingFromId]);

  // Mutations
  const createMutation = trpc.operationNatures.create.useMutation({
    onSuccess: async (data) => {
      // Salvar regras pendentes para a nova natureza
      for (const r of regras) {
        await createRegraMutation.mutateAsync({
          operationNatureId: data.id,
          csosnMesmoEstado: r.csosnMesmoEstado || undefined,
          csosnInterestadual: r.csosnInterestadual || undefined,
          cfopMesmoEstado: r.cfopMesmoEstado,
          cfopInterestadual: r.cfopInterestadual,
          gatilho: r.gatilho,
        });
      }
      toast.success("Natureza de operação criada com sucesso!");
      setIsDialogOpen(false);
      setEditingId(null);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao criar natureza de operação");
    },
  });

  const updateMutation = trpc.operationNatures.update.useMutation({
    onSuccess: () => {
      toast.success("Natureza de operação atualizada com sucesso!");
      setIsDialogOpen(false);
      setEditingId(null);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar natureza de operação");
    },
  });

  const toggleAtivoMutation = trpc.operationNatures.update.useMutation({
    onSuccess: () => {
      toast.success("Status atualizado com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar status");
    },
  });

  const createRegraMutation = trpc.operationNatures.createRegra.useMutation({
    onError: (error) => {
      toast.error(error.message || "Erro ao salvar regra");
    },
  });

  const deleteRegraMutation = trpc.operationNatures.deleteRegra.useMutation({
    onSuccess: () => {
      refetchRegras();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao excluir regra");
    },
  });

  const updateRegraMutation = trpc.operationNatures.updateRegra.useMutation({
    onSuccess: () => {
      setEditingRegraId(null);
      refetchRegras();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar regra");
    },
  });

  const resetForm = () => {
    setFormData({
      nome: "",
      emitenteId: 0,
      emitenteNome: "",
      tipo: "venda_mercadoria",
      movimentaEstoque: true,
      geraFinanceiro: true,
      observacoes: "",
      ativo: true,
      nfeProximoNumero: 1,
      nfeSerie: "001",
      tipoNota: "saida" as "entrada" | "saida",
      ambienteNfe: "homologacao" as "producao" | "homologacao",
      consumidorFinal: true,
      presenca: "presencial" as "nao_presencial" | "presencial" | "internet" | "outros",
      mod: 55,
      tpImp: 1,
      finNFe: 1,
      indIntermed: 0,
      indTot: 1,
      modFrete: 9,
    });
    setRegras([]);
    setNovaRegra({ ...emptyRegra });
    setEditingRegraId(null);
    setDuplicatingFromId(null);
  };

  const handleOpenDialog = (nature?: any) => {
    if (nature) {
      setEditingId(nature.id);
      setFormData({
        nome: nature.nome,
        emitenteId: nature.emitenteId || 0,
        emitenteNome: nature.emitenteNome || "",
        tipo: nature.tipo,
        movimentaEstoque: !!nature.movimentaEstoque,
        geraFinanceiro: !!nature.geraFinanceiro,
        observacoes: nature.observacoes || "",
        ativo: !!nature.ativo,
        nfeProximoNumero: nature.nfeProximoNumero || 1,
        nfeSerie: nature.nfeSerie || "001",
        tipoNota: nature.tipoNota || "saida",
        ambienteNfe: nature.ambienteNfe || "homologacao",
        consumidorFinal: nature.consumidorFinal != null ? !!nature.consumidorFinal : true,
        presenca: nature.presenca || "presencial",
        mod: nature.mod ?? 55,
        tpImp: nature.tpImp ?? 1,
        finNFe: nature.finNFe ?? 1,
        indIntermed: nature.indIntermed ?? 0,
        indTot: nature.indTot ?? 1,
        modFrete: nature.modFrete ?? 9,
      });
      setRegras([]); // será preenchido pelo useEffect quando regrasServidor carregar
    } else {
      setEditingId(null);
      resetForm();
      // Para clientes criando nova natureza: pré-preencher com o emitente do usuário (após reset)
      if (user?.role === 'cliente' && user.emitenteId) {
        const emitente = emitentes.find((e: any) => e.id === user.emitenteId);
        setFormData((prev) => ({
          ...prev,
          emitenteId: user.emitenteId!,
          emitenteNome: emitente?.razaoSocial || emitente?.nomeFantasia || `Emitente ${user.emitenteId}`,
        }));
      }
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.nome || !formData.emitenteId) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    const { emitenteNome, ...rest } = formData;
    const payload = {
      ...rest,
      observacoes: formData.observacoes || undefined,
      nfeSerie: formData.nfeSerie || undefined,
      nfeProximoNumero: formData.nfeProximoNumero || 1,
      ambienteNfe: formData.ambienteNfe,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleToggleAtivo = (id: number, currentStatus: boolean) => {
    toggleAtivoMutation.mutate({ id, ativo: !currentStatus });
  };

  const handleDuplicate = (nature: any) => {
    setEditingId(null);
    setFormData({
      nome: nature.nome + " (Cópia)",
      emitenteId: nature.emitenteId || 0,
      emitenteNome: nature.emitenteNome || "",
      tipo: nature.tipo,
      movimentaEstoque: !!nature.movimentaEstoque,
      geraFinanceiro: !!nature.geraFinanceiro,
      observacoes: nature.observacoes || "",
      ativo: !!nature.ativo,
      nfeProximoNumero: nature.nfeProximoNumero || 1,
      nfeSerie: nature.nfeSerie || "001",
      tipoNota: nature.tipoNota || "saida",
      ambienteNfe: nature.ambienteNfe || "homologacao",
      consumidorFinal: nature.consumidorFinal != null ? !!nature.consumidorFinal : true,
      presenca: nature.presenca || "presencial",
      mod: nature.mod ?? 55,
      tpImp: nature.tpImp ?? 1,
      finNFe: nature.finNFe ?? 1,
      indIntermed: nature.indIntermed ?? 0,
      indTot: nature.indTot ?? 1,
      modFrete: nature.modFrete ?? 9,
    });
    setRegras([]);
    setNovaRegra({ ...emptyRegra });
    setEditingRegraId(null);
    setDuplicatingFromId(nature.id); // useEffect vai buscar e preencher as regras
    setIsDialogOpen(true);
  };

  // Adicionar regra (para nova natureza: adiciona localmente; para edição: salva direto)
  const handleAdicionarRegra = async () => {
    if (!novaRegra.cfopMesmoEstado || novaRegra.cfopMesmoEstado.length !== 4) {
      toast.error("CFOP Mesmo Estado deve ter 4 dígitos");
      return;
    }
    if (!novaRegra.cfopInterestadual || novaRegra.cfopInterestadual.length !== 4) {
      toast.error("CFOP Interestadual deve ter 4 dígitos");
      return;
    }
    // Verificar gatilho duplicado
    const gatilhosUsados = regras.map((r: any) => r.gatilho);
    if (gatilhosUsados.includes(novaRegra.gatilho)) {
      toast.error(`Gatilho "${GATILHO_LABELS[novaRegra.gatilho]}" já está em uso nesta natureza`);
      return;
    }

    if (editingId) {
      // Salvar direto no servidor
      await createRegraMutation.mutateAsync({
        operationNatureId: editingId,
        csosnMesmoEstado: novaRegra.csosnMesmoEstado || undefined,
        csosnInterestadual: novaRegra.csosnInterestadual || undefined,
        cfopMesmoEstado: novaRegra.cfopMesmoEstado,
        cfopInterestadual: novaRegra.cfopInterestadual,
        gatilho: novaRegra.gatilho as any,
      });
      refetchRegras();
    } else {
      // Adicionar localmente (será salvo ao criar a natureza)
      setRegras([...regras, { ...novaRegra, id: Date.now() }]);
    }
    setNovaRegra({ ...emptyRegra });
  };

  const handleRemoverRegra = (regra: any) => {
    if (editingId && regra.id && typeof regra.id === "number" && regra.id < 1e12) {
      // ID real do banco
      deleteRegraMutation.mutate({ id: regra.id });
    } else {
      // ID temporário (nova natureza ainda não salva)
      setRegras(regras.filter((r: any) => r.id !== regra.id));
    }
  };

  const handleSalvarEdicaoRegra = () => {
    if (!editingRegraId) return;
    if (editingRegraData.cfopMesmoEstado.length !== 4 || editingRegraData.cfopInterestadual.length !== 4) {
      toast.error("CFOPs devem ter 4 dígitos");
      return;
    }
    updateRegraMutation.mutate({
      id: editingRegraId,
      csosnMesmoEstado: editingRegraData.csosnMesmoEstado || undefined,
      csosnInterestadual: editingRegraData.csosnInterestadual || undefined,
      cfopMesmoEstado: editingRegraData.cfopMesmoEstado,
      cfopInterestadual: editingRegraData.cfopInterestadual,
      gatilho: editingRegraData.gatilho as any,
    });
  };

  const filteredNatures = operationNatures.filter((nature: any) => {
    const search = searchTerm.toLowerCase();
    return (
      nature.nome.toLowerCase().includes(search) ||
      (nature.emitenteNome || "").toLowerCase().includes(search) ||
      TIPO_LABELS[nature.tipo as keyof typeof TIPO_LABELS].toLowerCase().includes(search)
    );
  });

  if (authLoading) {
    return <div className="flex items-center justify-center h-screen">Carregando...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Naturezas de Operação</h1>
          <p className="text-muted-foreground">Configure as naturezas de operação fiscal por emitente</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="mr-2 h-4 w-4" />
          Nova Natureza
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Buscar</CardTitle>
          <CardDescription>Filtre por nome, emitente ou tipo</CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="Digite para buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </CardContent>
      </Card>

      <div className="rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Ativo</th>
                <th className="px-4 py-3 text-left text-sm font-medium">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Nome</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Emitente</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Tipo</th>
                <th className="px-4 py-3 text-center text-sm font-medium">Regras</th>
                <th className="px-4 py-3 text-center text-sm font-medium">Mov. Estoque</th>
                <th className="px-4 py-3 text-center text-sm font-medium">Gera Financeiro</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredNatures.map((nature: any) => (
                <tr key={nature.id} className="hover:bg-muted/50">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleAtivo(nature.id, nature.ativo)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        nature.ativo ? "bg-green-500" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          nature.ativo ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm">{nature.id}</td>
                  <td className="px-4 py-3 text-sm font-medium">{nature.nome}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{nature.emitenteNome || "-"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${TIPO_COLORS[nature.tipo as keyof typeof TIPO_COLORS]}`}>
                      {TIPO_LABELS[nature.tipo as keyof typeof TIPO_LABELS]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-muted-foreground">{nature.totalRegras ?? 0} regras</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {nature.movimentaEstoque ? (
                      <Check className="h-4 w-4 text-green-600 mx-auto" />
                    ) : (
                      <X className="h-4 w-4 text-red-600 mx-auto" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {nature.geraFinanceiro ? (
                      <Check className="h-4 w-4 text-green-600 mx-auto" />
                    ) : (
                      <X className="h-4 w-4 text-red-600 mx-auto" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(nature)} title="Editar">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDuplicate(nature)} title="Duplicar">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog de Criação/Edição */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="!w-[85vw] !h-[90vh] !max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar" : "Nova"} Natureza de Operação</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Emitente *</Label>
              {user?.role === 'cliente' ? (
                <Input
                  value={formData.emitenteNome || (user.emitenteId ? `Emitente ${user.emitenteId}` : "")}
                  disabled
                  className="bg-muted"
                />
              ) : (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setIsEmitenteModalOpen(true)}
                >
                  {formData.emitenteNome || "Selecionar emitente..."}
                </Button>
              )}
            </div>

            <div>
              <Label>Nome da Natureza *</Label>
              <Input
                placeholder="Ex: Venda Consumidor Final - SP"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              />
            </div>

            <div>
              <Label>Tipo de Operação *</Label>
              <Select value={formData.tipo} onValueChange={(value: any) => setFormData({ ...formData, tipo: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="movEstoque"
                  checked={formData.movimentaEstoque}
                  onChange={(e) => setFormData({ ...formData, movimentaEstoque: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="movEstoque">Movimenta Estoque</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="geraFinanceiro"
                  checked={formData.geraFinanceiro}
                  onChange={(e) => setFormData({ ...formData, geraFinanceiro: e.target.checked })}
                  className="h-4 w-4"
                />
                <Label htmlFor="geraFinanceiro">Gera Financeiro</Label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo *</Label>
                <Select value={formData.tipoNota} onValueChange={(value: any) => setFormData({ ...formData, tipoNota: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entrada">Entrada</SelectItem>
                    <SelectItem value="saida">Saída</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ambiente *</Label>
                <Select value={formData.ambienteNfe} onValueChange={(value: any) => setFormData({ ...formData, ambienteNfe: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="producao">Produção</SelectItem>
                    <SelectItem value="homologacao">Homologação</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Consumidor Final *</Label>
                <Select value={formData.consumidorFinal ? "sim" : "nao"} onValueChange={(value) => setFormData({ ...formData, consumidorFinal: value === "sim" })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sim">Sim</SelectItem>
                    <SelectItem value="nao">Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Presença *</Label>
                <Select value={formData.presenca} onValueChange={(value: any) => setFormData({ ...formData, presenca: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nao_presencial">Não se aplica</SelectItem>
                    <SelectItem value="presencial">Presencial</SelectItem>
                    <SelectItem value="internet">Internet</SelectItem>
                    <SelectItem value="outros">Outros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Série NF-e</Label>
                <Input
                  placeholder="001"
                  maxLength={3}
                  value={formData.nfeSerie}
                  onChange={(e) => setFormData({ ...formData, nfeSerie: e.target.value })}
                />
              </div>
              <div>
                <Label>Próximo Número NF-e</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.nfeProximoNumero}
                  onChange={(e) => setFormData({ ...formData, nfeProximoNumero: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Modelo (mod)</Label>
                <Select value={String(formData.mod)} onValueChange={(v) => setFormData({ ...formData, mod: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="55">55 - NF-e</SelectItem>
                    <SelectItem value="65">65 - NFC-e</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Impressão DANFE (tpImp)</Label>
                <Select value={String(formData.tpImp)} onValueChange={(v) => setFormData({ ...formData, tpImp: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Retrato</SelectItem>
                    <SelectItem value="2">2 - Paisagem</SelectItem>
                    <SelectItem value="3">3 - Simplificado</SelectItem>
                    <SelectItem value="4">4 - NFC-e</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Finalidade NF-e (finNFe)</Label>
                <Select value={String(formData.finNFe)} onValueChange={(v) => setFormData({ ...formData, finNFe: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Normal</SelectItem>
                    <SelectItem value="2">2 - Complementar</SelectItem>
                    <SelectItem value="3">3 - Ajuste</SelectItem>
                    <SelectItem value="4">4 - Devolução</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Intermediador (indIntermed)</Label>
                <Select value={String(formData.indIntermed)} onValueChange={(v) => setFormData({ ...formData, indIntermed: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0 - Sem intermediador</SelectItem>
                    <SelectItem value="1">1 - Com intermediador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Compõe Total (indTot)</Label>
                <Select value={String(formData.indTot)} onValueChange={(v) => setFormData({ ...formData, indTot: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Sim</SelectItem>
                    <SelectItem value="0">0 - Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Modalidade Frete (modFrete)</Label>
                <Select value={String(formData.modFrete)} onValueChange={(v) => setFormData({ ...formData, modFrete: parseInt(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0 - Por conta do emitente</SelectItem>
                    <SelectItem value="1">1 - Por conta do destinatário</SelectItem>
                    <SelectItem value="2">2 - Por conta de terceiros</SelectItem>
                    <SelectItem value="9">9 - Sem frete</SelectItem>
                    <SelectItem value="10">De acordo com orçamento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Observações</Label>
              <Textarea
                rows={3}
                placeholder="Informações adicionais sobre esta natureza de operação..."
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
              />
            </div>

            {/* Seção de Regras de CFOP/CSOSN */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold">Regras de CFOP / CSOSN</h3>
                <span className="text-xs text-muted-foreground">{regras.length} regra(s) cadastrada(s)</span>
              </div>

              {/* Tabela de regras existentes */}
              {regras.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b bg-muted/50">
                      <tr>
                        <th className="px-2 py-2 text-left font-medium">CSOSN Mesmo Estado</th>
                        <th className="px-2 py-2 text-left font-medium">CSOSN Interestadual</th>
                        <th className="px-2 py-2 text-left font-medium">CFOP Mesmo Estado</th>
                        <th className="px-2 py-2 text-left font-medium">CFOP Interestadual</th>
                        <th className="px-2 py-2 text-left font-medium">Gatilho</th>
                        <th className="px-2 py-2 text-center font-medium">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {regras.map((regra: any) => (
                        <tr key={regra.id} className="hover:bg-muted/30">
                          {editingRegraId === regra.id ? (
                            <>
                              <td className="px-2 py-1">
                                <Input
                                  maxLength={4}
                                  className="h-7 text-xs"
                                  value={editingRegraData.csosnMesmoEstado}
                                  onChange={(e) => setEditingRegraData({ ...editingRegraData, csosnMesmoEstado: e.target.value })}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <Input
                                  maxLength={4}
                                  className="h-7 text-xs"
                                  value={editingRegraData.csosnInterestadual}
                                  onChange={(e) => setEditingRegraData({ ...editingRegraData, csosnInterestadual: e.target.value })}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <Input
                                  maxLength={4}
                                  className="h-7 text-xs"
                                  value={editingRegraData.cfopMesmoEstado}
                                  onChange={(e) => setEditingRegraData({ ...editingRegraData, cfopMesmoEstado: e.target.value })}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <Input
                                  maxLength={4}
                                  className="h-7 text-xs"
                                  value={editingRegraData.cfopInterestadual}
                                  onChange={(e) => setEditingRegraData({ ...editingRegraData, cfopInterestadual: e.target.value })}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <Select value={editingRegraData.gatilho} onValueChange={(v) => setEditingRegraData({ ...editingRegraData, gatilho: v })}>
                                  <SelectTrigger className="h-7 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {GATILHO_OPTIONS.map((g) => (
                                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-2 py-1 text-center space-x-1">
                                <Button size="sm" className="h-7 text-xs" onClick={handleSalvarEdicaoRegra}>Salvar</Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingRegraId(null)}>Cancelar</Button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-2 py-2">{regra.csosnMesmoEstado || "-"}</td>
                              <td className="px-2 py-2">{regra.csosnInterestadual || "-"}</td>
                              <td className="px-2 py-2">{regra.cfopMesmoEstado}</td>
                              <td className="px-2 py-2">{regra.cfopInterestadual}</td>
                              <td className="px-2 py-2">
                                <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">
                                  {GATILHO_LABELS[regra.gatilho] || regra.gatilho}
                                </span>
                              </td>
                              <td className="px-2 py-2 text-center space-x-1">
                                {editingId && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 text-xs"
                                    onClick={() => {
                                      setEditingRegraId(regra.id);
                                      setEditingRegraData({
                                        csosnMesmoEstado: regra.csosnMesmoEstado || "",
                                        csosnInterestadual: regra.csosnInterestadual || "",
                                        cfopMesmoEstado: regra.cfopMesmoEstado,
                                        cfopInterestadual: regra.cfopInterestadual,
                                        gatilho: regra.gatilho,
                                      });
                                    }}
                                  >
                                    Editar
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-red-600 hover:text-red-700"
                                  onClick={() => handleRemoverRegra(regra)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Formulário para adicionar nova regra */}
              <div className="border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground mb-2">Adicionar nova regra</p>
                <div className="grid grid-cols-6 gap-2 items-end">
                  <div>
                    <Label className="text-xs">CSOSN Mesmo Estado</Label>
                    <Input
                      maxLength={4}
                      placeholder="101"
                      className="h-8 text-sm"
                      value={novaRegra.csosnMesmoEstado}
                      onChange={(e) => setNovaRegra({ ...novaRegra, csosnMesmoEstado: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CSOSN Interestadual</Label>
                    <Input
                      maxLength={4}
                      placeholder="102"
                      className="h-8 text-sm"
                      value={novaRegra.csosnInterestadual}
                      onChange={(e) => setNovaRegra({ ...novaRegra, csosnInterestadual: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CFOP Mesmo Estado *</Label>
                    <Input
                      maxLength={4}
                      placeholder="5102"
                      className="h-8 text-sm"
                      value={novaRegra.cfopMesmoEstado}
                      onChange={(e) => setNovaRegra({ ...novaRegra, cfopMesmoEstado: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CFOP Interestadual *</Label>
                    <Input
                      maxLength={4}
                      placeholder="6102"
                      className="h-8 text-sm"
                      value={novaRegra.cfopInterestadual}
                      onChange={(e) => setNovaRegra({ ...novaRegra, cfopInterestadual: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Gatilho *</Label>
                    <Select value={novaRegra.gatilho} onValueChange={(v) => setNovaRegra({ ...novaRegra, gatilho: v })}>
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GATILHO_OPTIONS.map((g) => (
                          <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Button className="h-8 w-full" onClick={handleAdicionarRegra}>
                      <Plus className="h-4 w-4 mr-1" /> Adicionar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Emitente */}
      <Dialog open={isEmitenteModalOpen} onOpenChange={setIsEmitenteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Selecionar Emitente</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {emitentes.map((emitente: any) => (
              <Button
                key={emitente.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => {
                  setFormData({ ...formData, emitenteId: emitente.id, emitenteNome: emitente.razaoSocial });
                  setIsEmitenteModalOpen(false);
                }}
              >
                {emitente.razaoSocial}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
